/**
 * Run and Record — written once, framed by the blueprint.
 *
 * Open → invite → close → read → publish, using the Session app's canonical
 * facilitator actions verbatim. Two things vary by blueprint: what the result
 * groups are called, and whether the result asks who owns each line. Owners are
 * written as canonical commitments, so a Post-Mortem action item outlives the
 * meeting that produced it.
 *
 * Owners are picked from the workspace's members (roadmap S-10, ruling R15),
 * never typed, so the commitment lands on a real person's list. A blueprint
 * that does not own line by line asks who owns the next step of the result.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Check, Copy, ExternalLink, UserPlus } from 'lucide-react';
import { useSessionStore } from '@/stores/prioritization/sessionStore';
import { useToolSessionStore } from '@/stores/prioritization/toolSessionStore';
import { useCommitmentStore } from '@/stores/prioritization/commitmentStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useWorkspaceMembers } from '@/hooks/useWorkspaceMembers';
import { getCurrentWorkspaceId } from '@/lib/supabaseSync';
import { RESULT_OWNER_KEY, ownerCommitmentDescription, ownersFromCommitments } from './blueprintActions';
import {
  closeAndComputeResults,
  openForParticipants,
  publishResults,
} from '../session/sessionAppActions';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import { useActorDisplayMap } from '@/hooks/prioritization/useActorDisplayMap';
import { sessionPublishAuthorityLine, useSessionPublishAuthority } from '@/hooks/prioritization/useSessionPublishAuthority';
import {
  AppCard,
  EmptyState,
  Eyebrow,
  PrimaryButton,
  SecondaryButton,
  StatusChip,
} from '../components/primitives';
import { blueprintCandidates, blueprintResult } from './blueprintActions';
import type { AppBlueprint } from './types';

/** Who has the ball while an offline window is open, and when it closes (S-9). */
function windowLine(closesAt: Date | string | undefined): string {
  if (!closesAt) return 'your people — they are responding on their own time';
  const at = new Date(closesAt);
  if (Number.isNaN(at.getTime())) return 'your people — they are responding on their own time';
  if (at.getTime() <= Date.now()) return 'you — the window has closed; close it and read the result';
  const when = at.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  return `your people — they are responding on their own time, until ${when}`;
}

export default function RunScreen({ blueprint }: { blueprint: AppBlueprint }) {
  const { sessionId } = useParams<{ sessionId: string }>();
  const app = getAppDefinition(blueprint.appId)!;
  const { copy, guidance } = blueprint;

  const userId = useUserStore((s) => s.userId);
  const userName = useUserStore((s) => s.userName);
  const fetchSessionById = useSessionStore((s) => s.fetchSessionById);
  const session = useSessionStore((s) => s.sessions.find((x) => x.id === sessionId));
  // Open, close and publish are drawn only for someone the session RPCs accept
  // (internal_session_publish_authority, migration 704) — build contract §2.8.
  const mayHost = useSessionPublishAuthority(session);
  const hostNames = useActorDisplayMap([session?.facilitatorId]);
  const hostName = session?.facilitatorId ? hostNames[session.facilitatorId] ?? null : null;
  const subscribeToSessionToolSessions = useToolSessionStore(
    (s) => s.subscribeToSessionToolSessions,
  );
  const toolSessions = useToolSessionStore((s) => s.toolSessions);
  const participantResponses = useToolSessionStore((s) => s.participantResponses);
  const createCommitment = useCommitmentStore((s) => s.createCommitment);
  const commitments = useCommitmentStore((s) => s.commitments);
  const fetchCommitments = useCommitmentStore((s) => s.fetchFromSupabase);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [ownerFor, setOwnerFor] = useState<string | null>(null);
  const [ownerId, setOwnerId] = useState('');
  const { activeMembers } = useWorkspaceMembers(session?.workspaceId ?? getCurrentWorkspaceId());
  const resultIsIn = session?.state === 'RUN_COMPLETE' || session?.state === 'PUBLISHED';
  useEffect(() => {
    if (resultIsIn) void fetchCommitments();
  }, [resultIsIn, fetchCommitments]);
  // Owners already on record for this result (they outlive a reload).
  const owned = useMemo(
    () => (sessionId ? ownersFromCommitments(commitments, sessionId) : {}),
    [commitments, sessionId],
  );

  useEffect(() => {
    if (sessionId) void fetchSessionById(sessionId);
  }, [sessionId, fetchSessionById]);

  useEffect(() => {
    if (sessionId && session?.state === 'CRITERIA_FINALIZED') {
      void subscribeToSessionToolSessions(sessionId);
    }
  }, [sessionId, session?.state, subscribeToSessionToolSessions]);

  const submitted = useMemo(() => {
    const ids = toolSessions.filter((t) => t.sessionId === sessionId).map((t) => t.id);
    return new Set(
      participantResponses
        .filter((r) => ids.includes(r.toolSessionId) && r.isComplete)
        .map((r) => r.participantId),
    ).size;
  }, [toolSessions, participantResponses, sessionId]);

  const run = useCallback(
    async (action: () => Promise<{ success: boolean; error?: string }>) => {
      setBusy(true);
      setError(null);
      const result = await action();
      setBusy(false);
      if (!result.success) setError(result.error ?? 'Something went wrong.');
    },
    [],
  );

  const joinUrl = `${window.location.origin}/apps/session/${sessionId}`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy — select the link text instead.');
    }
  };

  const assignOwner = async (lineId: string, title: string) => {
    const member = activeMembers.find((m) => m.userId === ownerId);
    if (!member || !session) return;
    setBusy(true);
    setError(null);
    const created = await createCommitment({
      title,
      description: ownerCommitmentDescription(session, app.name, lineId === RESULT_OWNER_KEY ? undefined : lineId),
      commitmentType: 'internal',
      status: 'active',
      ownerId: member.userId,
      ownerName: member.name,
    });
    setBusy(false);
    if (created) {
      await fetchCommitments();
      setOwnerFor(null);
      setOwnerId('');
    } else {
      setError('That owner could not be recorded.');
    }
  };

  const ownerPicker = (lineId: string, title: string) => (
    <div className="mt-2 flex gap-2">
      <select
        value={ownerId}
        onChange={(e) => setOwnerId(e.target.value)}
        aria-label="Owner"
        className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 font-body text-sm dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
      >
        <option value="">Choose a member…</option>
        {activeMembers.map((m) => (
          <option key={m.userId} value={m.userId}>
            {m.name}
          </option>
        ))}
      </select>
      <PrimaryButton
        onClick={() => void assignOwner(lineId, title)}
        disabled={busy || !ownerId}
        className="!px-3 !py-1.5 !text-xs"
      >
        Record
      </PrimaryButton>
    </div>
  );

  if (!session) {
    return (
      <AppShell app={app} backTo={app.routeFamily}>
        <EmptyState title="Loading…" />
      </AppShell>
    );
  }

  const candidates = blueprintCandidates(session);
  const { top, rest } = blueprintResult(session, blueprint);
  const hasResult = top.length > 0;

  return (
    <AppShell app={app} backTo={app.routeFamily} backLabel={`Back to ${app.name}`}>
      <ScreenHeading
        title={session.name}
        subtitle={`${candidates.length} ${
          candidates.length === 1 ? copy.candidateNoun : copy.candidateNounPlural
        } · ${blueprint.methodName}`}
      />
      <BallLine
        ballWith={
          session.state === 'DRAFT'
            ? `${mayHost ? 'you' : 'the host'} — open it so people can weigh in`
            : session.state === 'CRITERIA_FINALIZED'
              ? blueprint.timing === 'sync'
                ? 'the room — they are deciding now'
                : windowLine(session.modeConfig?.closesAt)
              : `${mayHost ? 'you' : 'the host'} — read the result and publish`
        }
      />
      {!mayHost ? (
        <p className="mb-4 font-body text-sm text-gray-500 dark:text-[var(--text-ter)]">
          {sessionPublishAuthorityLine('this session', hostName)}
        </p>
      ) : null}

      <div className="space-y-4">
        {session.state === 'DRAFT' && mayHost ? (
          <AppCard>
            <Eyebrow>Open it</Eyebrow>
            <p className="mt-2 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
              {blueprint.timing === 'sync'
                ? 'Opening turns on the link so everyone can weigh in from their own device, together.'
                : 'Opening turns on the link. People respond independently, on their own time.'}
            </p>
            <div className="mt-4">
              <PrimaryButton
                disabled={busy}
                onClick={() =>
                  void run(() => openForParticipants(session, userId ?? '', userName ?? 'Owner'))
                }
              >
                {busy ? 'Opening…' : 'Open'}
              </PrimaryButton>
            </div>
          </AppCard>
        ) : null}

        {session.state !== 'DRAFT' ? (
          <AppCard>
            <Eyebrow>Invite</Eyebrow>
            <p className="mt-2 break-all rounded-md bg-gray-50 px-3 py-2 font-mono text-xs text-gray-700 dark:bg-[var(--surface-raised)] dark:text-[var(--text-sec)]">
              {joinUrl}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <SecondaryButton onClick={() => void copyLink()}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied' : 'Copy link'}
              </SecondaryButton>
              <SecondaryButton onClick={() => window.open(joinUrl, '_blank')}>
                <ExternalLink className="h-4 w-4" /> Open as participant
              </SecondaryButton>
            </div>
          </AppCard>
        ) : null}

        {session.state === 'CRITERIA_FINALIZED' && mayHost ? (
          <AppCard>
            <Eyebrow>Responses</Eyebrow>
            <p className="mt-2 font-heading text-2xl font-semibold text-gray-900 dark:text-[var(--text-pri)]">
              {submitted}
              <span className="ml-2 align-middle font-body text-sm font-normal text-gray-500 dark:text-[var(--text-ter)]">
                {submitted === 1 ? 'person has' : 'people have'} weighed in
              </span>
            </p>
            <div className="mt-4">
              <PrimaryButton
                disabled={busy || submitted === 0}
                onClick={() => void run(() => closeAndComputeResults(session))}
              >
                {busy ? 'Closing…' : 'Close & see the result'}
              </PrimaryButton>
            </div>
          </AppCard>
        ) : null}

        {hasResult ? (
          <>
            <AppCard className="!p-0">
              <div className="px-5 pt-5">
                <Eyebrow>
                  {copy.resultHeading}
                  {copy.restLabel ? ` · ${copy.topLabel}` : ''}
                </Eyebrow>
              </div>
              <ul className="mt-2">
                {top.map((row, i) => (
                  <li
                    key={row.id}
                    className="border-b border-gray-100 px-5 py-3 last:border-0 dark:border-[var(--rule)]"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-5 shrink-0 font-mono text-xs text-gray-400">{i + 1}</span>
                      <span className="min-w-0 flex-1 truncate font-body text-sm text-gray-900 dark:text-[var(--text-pri)]">
                        {row.name}
                      </span>
                      {blueprint.capturesOwners ? (
                        owned[row.id] ? (
                          <StatusChip label={owned[row.id]} tone="green" />
                        ) : (
                          <button
                            type="button"
                            onClick={() => setOwnerFor(ownerFor === row.id ? null : row.id)}
                            className="inline-flex items-center gap-1 rounded px-2 py-1 font-body text-xs text-gray-600 hover:bg-gray-100 dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]"
                          >
                            <UserPlus className="h-3.5 w-3.5" /> Who owns it?
                          </button>
                        )
                      ) : null}
                    </div>
                    {blueprint.capturesOwners && ownerFor === row.id ? ownerPicker(row.id, row.name) : null}
                  </li>
                ))}
              </ul>
              <p className="px-5 pb-4 pt-3 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                {guidance.resultAdvice}
              </p>
              {!blueprint.capturesOwners ? (
                <div className="border-t border-gray-100 px-5 pb-4 pt-3 dark:border-[var(--rule)]" data-testid="result-owner">
                  <div className="flex items-center gap-3">
                    <span className="flex-1 font-body text-sm text-gray-900 dark:text-[var(--text-pri)]">Who owns the next step?</span>
                    {owned[RESULT_OWNER_KEY] ? (
                      <StatusChip label={owned[RESULT_OWNER_KEY]} tone="green" />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setOwnerFor(ownerFor === RESULT_OWNER_KEY ? null : RESULT_OWNER_KEY)}
                        className="inline-flex items-center gap-1 rounded px-2 py-1 font-body text-xs text-gray-600 hover:bg-gray-100 dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]"
                      >
                        <UserPlus className="h-3.5 w-3.5" /> Name an owner
                      </button>
                    )}
                  </div>
                  {ownerFor === RESULT_OWNER_KEY && !owned[RESULT_OWNER_KEY]
                    ? ownerPicker(RESULT_OWNER_KEY, `Act on the result: ${session.name}`)
                    : null}
                </div>
              ) : null}
            </AppCard>

            {rest.length > 0 && copy.restLabel ? (
              <AppCard className="!p-0">
                <div className="px-5 pt-5">
                  <Eyebrow>
                    {copy.restLabel} · {rest.length}
                  </Eyebrow>
                </div>
                <ul className="mt-2">
                  {rest.map((row) => (
                    <li
                      key={row.id}
                      className="border-b border-gray-100 px-5 py-3 last:border-0 dark:border-[var(--rule)]"
                    >
                      <span className="min-w-0 flex-1 truncate font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
                        {row.name}
                      </span>
                    </li>
                  ))}
                </ul>
              </AppCard>
            ) : null}

            {session.state === 'RUN_COMPLETE' && mayHost ? (
              <div className="flex justify-end">
                <PrimaryButton
                  disabled={busy}
                  onClick={() => void run(() => publishResults(session.id))}
                >
                  {busy ? 'Publishing…' : 'Publish'}
                </PrimaryButton>
              </div>
            ) : null}
          </>
        ) : null}

        {error ? (
          <p className="font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">
            {error}
          </p>
        ) : null}
      </div>
    </AppShell>
  );
}
